
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { format } from 'date-fns';
import { WeeklyInsight } from './types/insights';
import { Button } from '@/components/ui/button';
import { Calendar, MessageSquare, ThumbsUp, ThumbsDown } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface InsightDetailsDialogProps {
  insight: WeeklyInsight | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InsightDetailsDialog({ insight, open, onOpenChange }: InsightDetailsDialogProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localFeedbackRating, setLocalFeedbackRating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!insight) return null;

  // Map UI feedback values to database values
  const mapFeedbackToDb = (uiRating: 'helpful' | 'not_helpful'): 'up' | 'down' => {
    return uiRating === 'helpful' ? 'up' : 'down';
  };

  // Map database feedback values to UI values
  const mapDbToFeedback = (dbRating: string | null): string | null => {
    if (!dbRating) return null;
    return dbRating === 'up' ? 'helpful' : 'not_helpful';
  };

  const handleFeedback = async (rating: 'helpful' | 'not_helpful') => {
    if (isSubmitting) return;
    
    setError(null);
    // Set local state for optimistic UI update
    setLocalFeedbackRating(rating);
    setIsSubmitting(true);
    
    try {
      if (!insight.id) {
        throw new Error("Insight ID is missing");
      }
      
      // Map UI feedback to database value
      const dbRating = mapFeedbackToDb(rating);
      
      const { error } = await supabase
        .from('weekly_reports')
        .update({ feedback_rating: dbRating })
        .eq('id', insight.id);
        
      if (error) {
        throw error;
      }
      
      toast({
        title: "Feedback Submitted",
        description: "Thank you for your feedback on this training insight.",
      });
    } catch (error) {
      console.error('Error submitting feedback:', error);
      // Revert to original state on error
      setLocalFeedbackRating(mapDbToFeedback(insight.feedback_rating));
      setError(`Failed to submit feedback: ${error instanceof Error ? error.message : String(error)}`);
      toast({
        title: "Failed to Submit Feedback",
        description: "There was an error saving your feedback. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Use local state if available, otherwise map the database value to UI value
  const currentRating = localFeedbackRating || mapDbToFeedback(insight.feedback_rating);
  
  // Filter out duplicate tags to ensure each tag only appears once
  const uniqueTags = insight.technique_tags ? 
    Array.from(new Set(insight.technique_tags)) : 
    [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-w-[95vw] max-h-[90vh] overflow-y-auto text-left p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl">
            Weekly Training Insights - {format(new Date(insight.week_start), 'MMM d, yyyy')}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="h-4 w-4" />
            <span>{insight.training_count || 0} sessions</span>
            <span>·</span>
            <span>{insight.training_minutes || 0} minutes</span>
          </div>

          <div className="prose dark:prose-invert max-w-none text-sm sm:text-base">
            {insight.ai_insights || insight.summary || "No insights available"}
          </div>

          {uniqueTags && uniqueTags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {uniqueTags.map((tag, index) => (
                <div key={`${tag}-${index}`} className="bg-secondary px-2 py-1 rounded-md text-xs">
                  {tag}
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center pt-4 border-t gap-4">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              <span className="text-sm text-muted-foreground">Was this insight helpful?</span>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                variant={currentRating === 'helpful' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleFeedback('helpful')}
                disabled={isSubmitting || currentRating === 'helpful'}
                className="flex-1 sm:flex-none touch-target-44"
              >
                <ThumbsUp className="h-4 w-4 mr-1" />
                Helpful
              </Button>
              <Button
                variant={currentRating === 'not_helpful' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleFeedback('not_helpful')}
                disabled={isSubmitting || currentRating === 'not_helpful'}
                className="flex-1 sm:flex-none touch-target-44"
              >
                <ThumbsDown className="h-4 w-4 mr-1" />
                Not Helpful
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
